import { ReactNode } from 'react'
import Stack from '@mui/material/Stack'
import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import AccessDenied from '@/components/ui/template/AccessDenied'
import { getFetchErrorStatus } from '@/utils/rtk-query'

export type QueryBoundaryProps = {
  isLoading: boolean
  isError?: boolean
  error?: FetchBaseQueryError | SerializedError
  /** Rendered while the query is loading. Defaults to a centered heartbeat loader. */
  loading?: ReactNode
  /** Rendered on a 403 response. Defaults to <AccessDenied />. */
  forbidden?: ReactNode
  /** Rendered for any other (non-403) error, or once neither loading nor erroring. Defaults to null. */
  fallback?: ReactNode
}

/**
 * Renders the non-success states of an RTK Query result (loading / forbidden /
 * generic error). Use it for the early-return "gate" while the queried entity is
 * not yet available:
 *
 *   const { data, isLoading, isError, error } = useGetThingQuery(id)
 *   if (!data) return <QueryBoundary isLoading={isLoading} isError={isError} error={error} />
 */
export function QueryBoundary({ isLoading, isError, error, loading, forbidden, fallback = null }: QueryBoundaryProps) {
  if (isLoading) {
    return (
      <>
        {loading ?? (
          <Stack flex={1} alignItems="center" justifyContent="center">
            <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
          </Stack>
        )}
      </>
    )
  }

  if (isError && getFetchErrorStatus(error) === 403) {
    return <>{forbidden ?? <AccessDenied />}</>
  }

  return <>{fallback}</>
}

export default QueryBoundary
